// @flow

import React, { Component } from 'react';
import { pure } from 'recompose';
import styled, { ThemeProvider } from 'styled-components';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import * as proActions from '../pros/actions';
import * as bookingActions from './actions';
import colors from '../common/components/ui/colors';
import themes from '../common/components/ui/themes';
import PopupFooter from '../common/components/popup/PopupFooter';
import { BookingService } from '../../api/booking-service';
import { BlockingLoader } from '../common/components/loader/BlockingLoader';
import InlineLoader from '../common/components/inlineloader';
import SetsterBookingDetails from './SetsterBookingDetails';
import BookingForm from './BookingForm';
import moment from 'moment';
import validate from 'validate.js';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import { getBookingDefaultState, getBookingConfiguration, SECOND_STEP, PAYMENT_TYPE_CASH, PAYMENT_TYPE_CREDIT_CARD } from '../../utils/booking-config';
import withLocalized from '../localization/withLocalized';
import { calculateVoucherDiscount } from '../../utils/price-utils';
import Interpolate from 'react-interpolate-component';
import { API_ROOT } from '../../api/constants';
import { GTMEcommerce } from '../../utils/gtm-utils';
import getUrlParameter from 'utils/urlParam'
import StoreCheckout from './StoreCheckout';

import { APP_THEME } from 'config'

type Props = {
  name: string
};
const makeBooking = (booking:Object) => {
  const route = `${API_ROOT}lesson_bookings?${encodeURI(window.location.origin)}`;
  const request = new Request(route, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(booking),
  });
  return fetch(request)
    .then(
      (response) => {
        if(response.status === 200) {
          return response;
        }
        throw new Error(`${response.status} ${response.statusText}`);
      }
    )
    .then(
      (response) => response.json()
    );
};

const initiatePayment = (paymentDetails:Object) => {
  const route = `${API_ROOT}payments?${encodeURI(window.location.origin)}`;
  const request = new Request(route, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(paymentDetails),
  });
  return fetch(request)
    .then(
      (response) => {
        if(response.status === 200) {
          return response;
        }
        throw new Error(`${response.status} ${response.statusText}`);
      }
    )
    .then(
      (response) => response.json()
    );
};

const BookingContainer = styled.div`
  padding: 2.5rem 0.375rem 0;
  box-sizing: border-box;
  flex-grow: 1;
  flex-shrink: 0;
`;

const StoreCheckoutWrap = styled.div`
  position: absolute;
  top: 10px;
  left: 0;
  width: 100%;
  bottom: 0;
  padding: 10px;
  z-index: 2;
  background: white;
`;

const StyledTerms = styled.div`
  padding: 0.75rem 0;
  font-size: 14px;
  line-height: 1.2em;
  color: #828282;

  a {
    color: ${colors.BRAND_GREEN};
  }
`;

const StyledLabel = styled.label`
  line-height: 2rem;
`;

const StyledCheckbox = styled.input`
  background: none;
  -webkit-appearance: none;
  border: 2px solid ${colors.BRAND_GREEN};
  width: 1rem;
  height: 1rem;
  vertical-align: middle;
  margin: -2px .25rem 0 0;

  &:after {
    content: '';
  }

  &:checked:after {
    content: '\u2713';
    color: ${colors.BRAND_GREEN};
    font-weight: bold;
    text-align: center;
    width: 100%;
    display: block;
    vertical-align: middle;
    height: 100%;
    line-height: 100%;
    font-size: 0.8rem;
  }
`;

class SetsterBooking extends Component<Props> {
  form: HTMLFormElement;
  stripeElement: HTMLElement;

  state;

  config;

  bookingService;

  constructor(props) {
    super(props);
    const defaultState = getBookingDefaultState()
    this.state = {...defaultState, userData: {...defaultState.userData, payment_type: undefined } }
    this.config = getBookingConfiguration();
    
    if(props.pro){
      this._addPaymentMethods(props.pro)
    }
    
  }

  componentWillMount() {
    this.bookingService = new BookingService();
    this.loadData();
  }

  componentWillReceiveProps(nextProps) {
    if(!this.props.pro && nextProps.pro){
      this._addPaymentMethods(nextProps.pro)
    }
  }

  _addPaymentMethods(pro){
    
    const { payment_methods = [] } = pro;

    if (payment_methods.includes('cash')) {
      this.config[SECOND_STEP].fields.push({
        key: 'payment_type',
        fieldType: 'select',
        label: false,
        options: [
          {
            key: PAYMENT_TYPE_CREDIT_CARD,
            label: 'booking.form.paymenttype_creditcart'
          },
          {
            key: PAYMENT_TYPE_CASH,
            label: 'booking.form.paymenttype_cash'
          },
        ]
      });
    }

    this.config[SECOND_STEP].fields.push({
      key: 'voucher',
      fieldType: 'voucher',
      label: 'booking.form.voucher',
      props: {
        onCodeValid: this._calculatePricingWithVoucher,
        onCodeInvalid: this._removeVoucherPricing,
        getVoucherPrice: this._getVoucherPrice,
        getVoucherMetaData: this._getVoucherMetaData,
        getVoucherCustomerObject: this._getVoucherCustomerObject
      }
    });
  }

  _calculatePricingWithVoucher = (result) => {
    const { price } = this.state;
    this.setState({
      voucherDiscount: result,
      price: calculateVoucherDiscount(price, result)
    });
  };

  _removeVoucherPricing = () => {
    const { service } = this.state;
    this.setState({
      voucherDiscount: null,
      price: service.price,
    });
  };

  _getVoucherPrice = () => {
    const { price } = this.state;
    if (price) {
      return price.price_value_original ? price.price_value_original : price.price_value;
    } else {
      return undefined;
    }
  };

  _getVoucherMetaData = () => {
    const { pro } = this.props;
    const { userData, service } = this.state;
    return {
      organizer: pro ? parseInt(pro.id, 10) : -1,
      bookingType: 'lesson',
      email: userData.email,
      serviceID: service.id,
      currency: this._resolveCurrencyFromProSettings().toUpperCase(),
    };
  };

  _getVoucherCustomerObject = () => {
    const { userData } = this.state;
    return {
      name: `${userData.first_name} ${userData.last_name}`,
      source_id: userData.email,
      email: userData.email,
    }
  };

  loadData() {
    const {
      serviceId,
      time
    } = this.props.match.params;

    const {
      pro: {
        location_id = undefined,
        calendar_uid = undefined,
      } = {}
    } = this.props

    const reservation = [moment(time)];

    if (reservation && serviceId) {

      this.setState({ reservation, isLoading: true }, () => {
        this.bookingService
          .getServices([serviceId])
          .then(services => {

            const service = services[serviceId];

            this.bookingService
              .getAvailabilityForDate(serviceId, reservation[0], service, location_id, calendar_uid)
              .then(currentDay => {
                this.setState({
                  currentDay,
                  reservation,
                  price: service.price,
                  service,
                  isLoading: false
                });
              });
          });
      });
    }
  }

  handleChange = (event: Event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    const field = event.target.name;
    const userData = this.state.userData;
    userData[field] = event.target.value;

    const errors = validate(userData, this.config[this.state.step].constraints) || {};
    const touchedFields = [
      ...this.state.touchedFields.filter((item) => item !== field),
      field
    ];

    const isValid = isEmpty(errors);

    this.setState({
      errors,
      touchedFields,
      isValid,
      userData
    });
  };

  _initializeStripePayment({order_number, success_url, price, client_secret}) {
    this.setState({
      stripe_order_number: order_number,
      stripe_success_url: success_url,
      stripe_client_secret: client_secret
    }, () => {
      if (parseInt(price, 10) !== 0){
        //this.stripeElement.click();
      }
      else{ // zero price, paid with voucher
        this._onStripeToken({})
      }
    });
  }

  _onStripeToken = (data) => {
    const { history } = this.props;
    data.booking_id = this.state.stripe_order_number;

    this.setState({stripePaymentProcessing: true});

    const request = new Request(this.state.stripe_success_url, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(data),
    });
    return fetch(request)
      .then(response => {
        this.setState({
          stripePaymentProcessing: false,
        }, () => {
          if(response.status === 200) {
            const transaction = GTMEcommerce.formLessonTransactionPayload(data.booking_id, this.state.price, this.state.service);
            GTMEcommerce.asyncGTMEcommerceTrack(transaction);
            history.push(`${this.props.location.pathname}/complete`);
          } else {
            history.push(`${this.props.location.pathname}/error`);
          }
        });
      }).catch(() => {
        alert('There was a problem processing the payment. Please try again later.');
      });
  };

  _onStripeClose = () => {
    this.setState({
      isSubmitting: false,
      isLoading: false,
    });
  };

  _resolveLocaleFromLocalization() {
    const { localeConfig } = this.props;
    const locale = localeConfig('locale');
    return locale.substring(0, locale.indexOf('_')) === 'en' ? 'en' : 'auto';
  }

  _resolveCurrencyFromLocalization() {
    const { localeConfig } = this.props;
    return localeConfig('units.currency.identifier');
  }
  
  _resolveCurrencyFromProSettings() {
    const { localeConfig } = this.props;
    const site_default_currency = localeConfig('units.currency.identifier');
    const { pro: {default_currency} } = this.props;
    return default_currency || site_default_currency;
  }

  _handlePaymentAction = (response) => {

    const {
      order_number,
      payment_url,
      payment_gateway,
      price,
    } = response;

    if (payment_url) {
      const transaction = GTMEcommerce.formLessonTransactionPayload(order_number, this.state.price, this.state.service);
      GTMEcommerce.pageLoadGTMEcommerceTrack(transaction);

      /*
        If payment was made with voucher the user no longer needs to be redirected
        to the payment service. Instead we call backend with verification and continue
        the user to success-page.
       */
      if (price === 0) {
        fetch(payment_url)
          .then(() => {
            this.props.history.push(`${this.props.location.pathname}/complete`);
          });
      } else {
        window.location.href = payment_url;
      }
    } else if (payment_gateway === 'stripe') {
      if (order_number !== undefined) {
        this._initializeStripePayment(response);
      }
    }
  };

  handleSubmit = (event: Event) => {
    event.preventDefault();

    if (!this.state.isValid) return;

    const step = this.state.step;
    if(step === 0) {
      this.setState({
        step: step+1,
        isValid: false
      });
      return;
    }

    const receive_newsletter = document.querySelector('#opt-in').checked ? 1 : 0;
    const handicap = this.state.userData.handicap;// !== '-' ? this.state.userData.handicap : '';
    const start_date = this.state.reservation[0]; // we only have one for now
    const payment_type = this.state.userData.payment_type;

    const {
      pro: {
        calendar_uid = undefined
      } = {}
    } = this.props

    const {
      state: {
        service: {
          resource_id = undefined
        } = {}
      } = {}
    } = this

    let sales_channel = 'web';
    if(getUrlParameter('saleschannel')){
      sales_channel = getUrlParameter('saleschannel')
    }

    const postData = merge({
      service_id: this.state.service.id,
      start_time: start_date.format('YYYY-MM-DD HH:mm:ss'),
      end_time: moment(start_date).add(this.state.service.duration, 'minutes').format('YYYY-MM-DD HH:mm:ss'),
      receive_newsletter,
      organizer: this.props.pro.id,
      voucher_code: (this.state.voucherDiscount && this.state.voucherDiscount.code) || '',
      payment_type: payment_type,
      calendar_uid,
      resource_id,
      sales_channel,
    }, this.state.userData, {handicap});

    this.setState({
      isSubmitting: true
    });
    /*
      Payload:
         {
           "email": "tomi.mikola@mailinator.com",
           "first_name": "Teppo",
           "last_name": "Testaaja",
           "handicap": "16",
           "phone": "04012345678",
           "gender": "male",
           "receive_newsletter": "0",
           "service_id": "45433",
           "start_time": "2017-08-24 10:00:00",
           "end_time": "2017-08-24 12:00:00",
           "organizer": "189"
         }
     */

    const currentLocation = window.location.href;
    const success_url = `${currentLocation}/complete`;
    const failure_url = `${currentLocation}/error`;

    makeBooking(postData).then((response) => {
      if(response.id !== undefined) {
        const booking_id = response.id;
        const payment_type = this.state.userData.payment_type;
        if (payment_type && payment_type === PAYMENT_TYPE_CASH) {
          this.props.history.push(`${this.props.location.pathname}/complete`);
          return
        }
        initiatePayment({
          booking_id,
          success_url,
          failure_url
        })
        .then(this._handlePaymentAction)
        .catch(() => {
          this.props.history.replace(`${this.props.location.pathname}/error`);
        })
      }
    }).catch((e) => {
      this.props.history.replace(`${this.props.location.pathname}/error`);
    });
  }

  render() {
    const { localized, pro } = this.props;
    
    if(this.state.hasFetchError) {
      return <p>Error fetching service details.</p>
    }

    if(!pro){
      return <InlineLoader />
    }

    const payment_type = 'userData' in this.state && this.state.userData.payment_type;
    const stepSubmitLabel = this.state.step === 0 ? localized('booking.form.label_next') : (payment_type && payment_type === PAYMENT_TYPE_CASH) ? localized('booking.form.label_submit_cash') : localized('booking.form.label_submit');
    const fields = this.config[this.state.step].fields;
    const shouldShowTermsLink = this.state.step === 1;
    const shouldFocusFirstField = false;

    return (
      <div>
        {!this.state.isLoading && (
          <BookingContainer>
            <SetsterBookingDetails service={this.state.service} reservation={this.state.reservation} pro={pro} price={this.state.price} />
            <ThemeProvider theme={themes[APP_THEME]}>
              <div>
                <BookingForm
                  disabled={!this.state.isValid || this.state.isSubmitting}
                  fields={fields}
                  touchedFields={this.state.touchedFields}
                  errors={this.state.errors}
                  onChange={this.handleChange}
                  onSubmit={this.handleSubmit}
                  submitLabel={stepSubmitLabel}
                  shouldFocusFirstField={shouldFocusFirstField}
                >
                  {shouldShowTermsLink && (
                    <StyledTerms>
                      <p>
                        <Interpolate
                          with={{
                            terms: <a href="/golfhubber_terms.pdf" target="_blank">{localized('booking.form.label_terms_link')}</a>
                          }}
                          format={localized('booking.form.label_terms')}
                        />
                        <br />
                        <StyledLabel htmlFor="opt-in">
                          <StyledCheckbox type="checkbox" name="opt-in" id="opt-in" /> {localized('booking.form.label_newsletter_subscription')}
                        </StyledLabel>
                      </p>
                    </StyledTerms>
                  )}

                </BookingForm>
                {this.state.stripe_client_secret && 
                  <StoreCheckoutWrap>
                    <StoreCheckout
                      loading={this.state.stripePaymentProcessing}
                      onSuccess={
                        (data) => {
                          this._onStripeToken(data)
                        }
                      }
                      client_secret={this.state.stripe_client_secret}
                      user={this.state.userData}
                    />
                  </StoreCheckoutWrap>
                }
              </div>
            </ThemeProvider>
            <PopupFooter
              linkLabel={localized('booking.form.label_help')}
              linkTarget={{
                pathname: `${this.props.match.url}/help`,
                state: { modal: true }
              }}
            />
          </BookingContainer>
        )}
        <BlockingLoader isLoading={this.state.isLoading || this.state.isSubmitting || this.state.stripePaymentProcessing} />
      </div>
    );
  }
}

const mapStateToProps = (state, { match }) => {

  const {
    entities: { pros }
  } = state;

  let id = match.params.id || match.params.machine_name;

  /*
   When url is used with server side machine_name we need to resolve accordingly
   */
  if (isNaN(parseInt(id, 10)) ) {
    for (const proId in pros) {
      if ( pros[proId].hasOwnProperty('machine_name') && pros[proId].machine_name === id ) {
        id = pros[proId].id;
      }
    }
  }

  return {
    pro: pros[id] || undefined,
    id
  }
};

SetsterBooking = withRouter(connect(
  mapStateToProps,
  Object.assign({}, proActions, bookingActions),
)(SetsterBooking));

export default pure(withLocalized(SetsterBooking));



// WEBPACK FOOTER //
// ./src/modules/booking/SetsterBooking.js